// HER DASHBOARD ITEM, SURFACE 3, CLAUSES 1 AND 2 / A2's FIRST ACT — **THE ALERT SURFACE.**
//
// Her clause 1 takes the failed-readings tile off Analytics; her clause 2 says where it goes: *the
// failure rows feed the alert surface, clearable, opening into the member / reading / cause detail
// WITH THE HEAL ACTION.* A2's law over the top of it: *every alert is a door — an alert she cannot
// act on does not render.*
//
// ================================================================================================
// **FOUR THINGS THIS COMPONENT DELIBERATELY DOES NOT DO, EACH OF WHICH IS HOW AN ALERT SCREEN
// FAILS.**
//
//   1. **IT COMPOSES NO SENTENCE OF ITS OWN AND COUNTS NOTHING.** Every line comes out of
//      `src/standingAlerts.js`, which imports nothing and which a FAST fixture LOADS and RUNS. A
//      screen that decided its own states would be a second answer to *is this still happening*, and
//      the copy that drifted would be the one she reads (R28/R29).
//   2. **IT NEVER TREATS AN UNREADABLE PAYLOAD AS A QUIET NIGHT.** `null` is a read that refused and
//      says so; `[]` is an answer. Collapsing them puts *"Nothing is going wrong right now."* on the
//      screen of an owner whose database was busy — her own *never read green over failing members*
//      clause, broken on the surface built to serve it (R18/R65).
//   3. **CLEARING DOES NOT REMOVE ANYTHING**, and the control says so in its own words: it is
//      *Mark as seen*, never *Dismiss*. *She has seen it* and *it has gone away* are different facts
//      and only the second takes a row off this screen. A cleared alert stays, labelled, until the
//      condition stops — and comes back as new if it grows past what she acknowledged.
//   4. **IT NEVER OFFERS THE HEAL WITHOUT NAMING WHAT IT COSTS.** That action buys a geocode per
//      member, on a metered vendor, and it is the one control on this dashboard that spends money.
//      The sentence rides the CLASS, so a button can never be drawn without one.
// ================================================================================================
import { useCallback, useEffect, useState } from "react";
import { loadOwnerData, postOwnerAction, ownerLoadNote, ownerActionNote } from "../lib/owner-load";
import {
  alertLine, alertRows, ALERT_OPEN, ALERT_RESOLVED, ALERT_SEEN_NOTE, ALERT_CLEAR_LABEL,
} from "../standingAlerts.js";
// CE-4's OWN SENTENCES, AT THEIR NEW ADDRESS. Her clause 1 moved the failures off Analytics, and
// this is the surface they moved TO — so the summary line, the could-not-be-read line and the
// nothing-here line come from the law that has always owned them rather than from three new strings
// typed here (R28/R29; the first draft of this file invented a fourth spelling of the last one).
import { failureSentence, failureLine, FAILURES_UNREAD, FAILURES_CLEAR } from "../generationFailures.js";

/** One row of the detail her alert opens into: her own three words — member, reading, cause. */
export type FailureRow = {
  id: string;
  userId: string;
  member: string;
  email: string;
  kind: string;
  label: string;
  reason: string;
  at: string;
  healAttempts: number;
  birthplace: string;
  hasCoordinates: boolean;
};
/** A standing alert exactly as the store shapes it. The STATE is derived, never sent. */
export type AlertRow = {
  id: string;
  class: string;
  count: number;
  members: number;
  firstSeenAt: string;
  lastSeenAt: string;
  clearedAt: string;
  clearedBy: string;
  clearedCount: number | null;
  resolvedAt: string;
};
type Payload = { alerts: AlertRow[] | null; failures: FailureRow[] | null; archive: boolean };

const stamp = (iso: string) => (iso ? new Date(iso).toLocaleString() : "");

export function OwnerAlerts({ onDenied }: { onDenied: () => void }) {
  // `undefined` is a payload in flight and holds still; `null` is one that arrived and could not be
  // read. Three states, and the law above knows all three by name.
  const [alerts, setAlerts] = useState<AlertRow[] | null | undefined>(undefined);
  const [failures, setFailures] = useState<FailureRow[] | null>(null);
  const [archive, setArchive] = useState(false);
  const [note, setNote] = useState("");
  const [open, setOpen] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async (view: boolean) => {
    const answer = await loadOwnerData<Payload>(`/api/owner/alerts${view ? "?view=archive" : ""}`);
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    if (answer.ok) {
      setAlerts(answer.data.alerts ?? null);
      setFailures(answer.data.failures ?? null);
    } else {
      // R65: a refused read is NOT an empty screen. `null` reaches the law, which says so.
      setAlerts(null);
      setFailures(null);
    }
    setNote(ownerLoadNote(answer));
  }, [onDenied]);

  useEffect(() => { void load(archive); }, [load, archive]);

  // The archive asks the SAME mapper for the resolved set rather than a second one, so a record row
  // and a live row can never say two different things about the same class.
  const rows = archive ? alertRows(alerts, { states: [ALERT_RESOLVED] }) : alertRows(alerts);
  const line = archive ? "" : alertLine(alerts);

  const clear = async (id: string) => {
    setBusy(id);
    const answer = await postOwnerAction<{ sentence?: string }>("/api/owner/alert-clear", { id });
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    setNote(ownerActionNote(answer));
    setBusy("");
    await load(archive);
  };

  const heal = async (userId: string) => {
    setBusy(userId);
    const answer = await postOwnerAction<{ sentence?: string }>("/api/owner/alert-heal", { userId });
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    setNote(ownerActionNote(answer));
    setBusy("");
    await load(archive);
  };

  return (
    <div className="owner-alerts">
      <h3 className="owner-overview__h">Alerts</h3>
      <p className="owner-page__note owner-alerts__line">{alerts === undefined ? "..." : line}</p>
      {note ? <p className="owner-page__note owner-alerts__note">{note}</p> : null}

      {/* Her *Archive* door, surface 1's own shape: what has gone away, out of the way, still
          readable on the rare day she needs it. It is a different QUESTION from the live screen and
          never a filter on it — the live list is what is happening now. */}
      <button type="button" className="owner-alerts__archive" onClick={() => { setArchive(!archive); setOpen(""); }}>
        {archive ? "Back to what is happening" : "Archive"}
      </button>

      <ul className="owner-alerts__list">
        {(rows || []).map((r) => (
          <li key={r.id} className={r.state === ALERT_OPEN ? "owner-alerts__item" : "owner-alerts__item is-seen"}>
            <div className="owner-alerts__head">
              <strong>{r.say}</strong>
              <span>{r.text}</span>
              {/* FIRST SEEN / STILL PRESENT — A1's own three facts, on the row that carries them.
                  A standing finding she has been looking at for six days and one that started last
                  night want different things from her, and only these two dates tell them apart. */}
              <em>{`First seen ${stamp(r.firstSeen)} · last checked ${stamp(r.lastSeen)}`}</em>
              {r.state !== ALERT_OPEN ? <em className="owner-alerts__seen">{ALERT_SEEN_NOTE}</em> : null}
            </div>
            <div className="owner-alerts__acts">
              {/* A2: the alert IS the door. Opening it is what her clause 2 calls the detail. */}
              <button type="button" onClick={() => setOpen(open === r.id ? "" : r.id)}
                aria-expanded={open === r.id} aria-label={`${r.say} — open the detail`}>
                {open === r.id ? "Close" : "Open"}
              </button>
              {!archive && r.state === ALERT_OPEN ? (
                <button type="button" disabled={busy === r.id} onClick={() => void clear(r.id)}>
                  {ALERT_CLEAR_LABEL}
                </button>
              ) : null}
            </div>

            {open === r.id && r.detail === "failures" ? (
              <div className="owner-alerts__detail">
                {/* THREE STATES HERE TOO. A detail that could not be read is not an alert with
                    nothing behind it — and an alert with nothing behind it is exactly what A2's law
                    refuses, so it may never be what a refused read looks like. */}
                {failures === null ? (
                  <p className="owner-page__note">{FAILURES_UNREAD}</p>
                ) : (
                  <>
                  {/* The heading is CE-4's own summary sentence, composed from the numbers ALREADY
                      ON THE ROW — never a second count of the same thing (R28/R29). */}
                  <p className="owner-page__note owner-failures__line">
                    {failureLine({ failures: r.count, membersAffected: r.members })}
                  </p>
                  <table className="owner-table owner-alerts__table">
                    <thead>
                      <tr><th>Member</th><th>Reading</th><th>Cause</th><th>When</th><th /></tr>
                    </thead>
                    <tbody>
                      {failures.map((f) => (
                        <tr key={f.id}>
                          <td>{f.member || f.email}</td>
                          <td>{f.label || f.kind}</td>
                          {/* The cause is her SENTENCE and never the key. `failureSentence` is the
                              one translation of it, shared with the patrol and the record. */}
                          <td>{failureSentence(f.reason)}</td>
                          <td>{stamp(f.at)}</td>
                          <td>
                            {/* **THE HEAL, AND IT SPENDS.** Offered only where there is something
                                for the remedy to work with: a member with a birthplace and no
                                coordinates behind it. A button on a row it cannot help is A2's dead
                                box wearing an action. */}
                            {r.heals && f.birthplace && !f.hasCoordinates ? (
                              <button type="button" disabled={busy === f.userId}
                                title={r.healCosts}
                                onClick={() => void heal(f.userId)}>
                                {r.healSays}
                              </button>
                            ) : null}
                            {f.healAttempts ? <em>{`tried ${f.healAttempts} ${f.healAttempts === 1 ? "time" : "times"}`}</em> : null}
                          </td>
                        </tr>
                      ))}
                      {!failures.length ? (
                        <tr><td colSpan={5}>{FAILURES_CLEAR}</td></tr>
                      ) : null}
                    </tbody>
                  </table>
                  </>
                )}
                {r.heals ? <p className="owner-page__note owner-alerts__cost">{r.healCosts}</p> : null}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {rows && !rows.length && alerts !== undefined ? (
        <p className="owner-page__note">{archive ? "Nothing has been resolved yet." : ""}</p>
      ) : null}
    </div>
  );
}
